import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import * as XLSX from "xlsx";
import { useAuth } from "../context/AuthContext";
import { usePermissions } from "../context/PermissionsContext";
import { useExportSuccess } from "../context/ExportSuccessContext";
import { customerAPI } from "../api/apiService";
import EmptyState from "../components/EmptyState";
// eslint-disable-next-line no-unused-vars
import { Plus, Edit2, Trash2, Search, Phone, CheckCircle, AlertCircle, Download, Upload, Calendar, ShoppingBag, IndianRupee, History, User, ArrowRight, Mail, LayoutGrid, Clock, X } from "lucide-react";

export default function CustomersPage() {
  const { userRole } = useAuth();
  const { hasPermission } = usePermissions();
  const [customers, setCustomers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dateFilterActive, setDateFilterActive] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [notification, setNotification] = useState(null);
  const exportSuccess = useExportSuccess();
  const [loading, setLoading] = useState(false);
  const [backendConnected, setBackendConnected] = useState(true);
  const [viewingCustomer, setViewingCustomer] = useState(null);

  // Fetch customers and invoices from backend on mount
  useEffect(() => {
    fetchData();

    // Listen for customer added event from POS Billing page
    const handleCustomerAdded = (event) => {
      fetchData(); // Refresh customer list
    };

    window.addEventListener("customerAdded", handleCustomerAdded);

    return () => {
      window.removeEventListener("customerAdded", handleCustomerAdded);
    };
  }, []);

  const fetchData = async () => {
    setLoading(true);
    await fetchCustomers();
    setLoading(false);
  };

  async function fetchCustomers() {
    try {
      const response = await customerAPI.getAllCustomers();

      // Django Rest Framework Pagination returns { results: [], count: ... }
      // Or standard list [ ... ]
      // Or { data: ... } wrapper
      let customersData = response.data?.results || response.data?.data || response.data;

      // If it's nested like {customers: [...]}, extract the array
      if (customersData && !Array.isArray(customersData) && customersData.customers) {
        customersData = customersData.customers;
      }

      if (Array.isArray(customersData)) {
        // Map backend fields to frontend expected fields
        const mappedCustomers = customersData.map(c => {
          // Parse joined_date properly - handle both ISO strings and date strings
          let joinedDate = '';
          if (c.joined_date) {
            joinedDate = typeof c.joined_date === 'string' ? c.joined_date.split('T')[0] : new Date(c.joined_date).toISOString().split('T')[0];
          } else if (c.created_at) {
            joinedDate = typeof c.created_at === 'string' ? c.created_at.split('T')[0] : new Date(c.created_at).toISOString().split('T')[0];
          } else {
            joinedDate = new Date().toISOString().split('T')[0];
          }

          return {
            ...c,
            joined: joinedDate,
            isActive: c.status === 'active'
          };
        });
        setCustomers(mappedCustomers);
        setBackendConnected(true);
      } else {
        setCustomers([]);
      }
    } catch (error) {
      setBackendConnected(false);
      showNotification("Failed to load customers from server", "error");
    }
  }

  const [newCustomer, setNewCustomer] = useState({
    name: "",
    email: "",
    phone: "",
    joined: new Date().toISOString().split("T")[0],
    isActive: true,
  });

  // Show notification for a few seconds
  const showNotification = (message, type = "success") => {
    // Ensure message is always a string
    const messageStr = typeof message === 'string' ? message : String(message);
    setNotification({ message: messageStr, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Get customer financial data - simplified
  function enrichCustomerWithInvoiceData(customer) {
    return {
      ...customer,
      invoiceCount: customer.total_orders || 0,
      totalSpent: parseFloat(customer.total_spent || 0)
    };
  }

  const filtered = Array.isArray(customers) ? customers.filter((c) => {
    const matchesSearch = (c.name && c.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (c.email && c.email.toLowerCase().includes(searchTerm.toLowerCase()));

    // Only apply date filtering if it's active
    let matchesDate = true;
    if (dateFilterActive && c.joined) {
      const customerDate = new Date(c.joined);
      matchesDate =
        customerDate.getDate() === selectedDate.getDate() &&
        customerDate.getMonth() === selectedDate.getMonth() &&
        customerDate.getFullYear() === selectedDate.getFullYear();
    }

    return matchesSearch && matchesDate;
  }) : [];

  async function saveCustomer(updated) {
    try {
      // Find the original customer to check if status changed
      const originalCustomer = customers.find(c => c.id === updated.id);
      const originalStatus = originalCustomer?.isActive;
      const newStatus = updated.isActive;

      // Check if ONLY status is changing (not name, email, phone)
      const isStatusOnlyChange =
        originalCustomer &&
        originalCustomer.name === updated.name &&
        originalCustomer.email === updated.email &&
        originalCustomer.phone === updated.phone &&
        originalStatus !== newStatus;

      // Prepared payload
      const payload = {
        name: updated.name,
        email: updated.email,
        phone: updated.phone,
        status: updated.isActive ? 'active' : 'inactive',
        // Preserve other fields if needed, but remove UI-only ones like spent/totalBills if they are not in model
      };

      const response = await customerAPI.updateCustomer(updated.id, payload);
      const data = response.data?.data || response.data;
      if (data) {
        // Parse joined_date properly
        let joinedDate = '';
        if (data.joined_date) {
          joinedDate = typeof data.joined_date === 'string' ? data.joined_date.split('T')[0] : new Date(data.joined_date).toISOString().split('T')[0];
        } else if (data.created_at) {
          joinedDate = typeof data.created_at === 'string' ? data.created_at.split('T')[0] : new Date(data.created_at).toISOString().split('T')[0];
        }

        // Map back
        const mappedData = {
          ...data,
          isActive: data.status === 'active',
          joined: joinedDate || updated.joined
        };
        const updatedCustomers = customers.map((it) => (it.id === updated.id ? mappedData : it));
        setCustomers(updatedCustomers);

        // Show context-specific notification
        if (isStatusOnlyChange) {
          if (newStatus) {
            showNotification(`✅ Successfully activated`);
          } else {
            showNotification(`⚠️ Successfully deactivated`);
          }
        } else {
          showNotification(`✅ Updated successfully`);
        }
      }
      setEditing(null);
    } catch (error) {
      showNotification("Failed to update customer", "error");
    }
  }

  function deleteCustomer(id) {
    const customer = customers.find(c => c.id === id);
    setDeleteConfirm({ id, name: customer?.name });
  }

  async function confirmDelete(id) {
    const customer = customers.find(c => c.id === id);
    const customerName = customer?.name;

    try {
      const response = await customerAPI.deleteCustomer(id);

      const updatedCustomers = customers.filter((it) => it.id !== id);
      setCustomers(updatedCustomers);
      showNotification(`${customerName} deleted successfully`, "success");
    } catch (error) {

      let errorMessage = "Failed to delete customer";

      if (error.response?.status === 403) {
        errorMessage = "You don't have permission to delete this customer";
      } else if (error.response?.status === 404) {
        errorMessage = "Customer not found";
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      showNotification(errorMessage, "error");
    }
    setDeleteConfirm(null);
  }

  async function addNewCustomer() {
    const phoneVal = newCustomer.phone.trim();
    const nameVal = newCustomer.name.trim();
    const emailVal = newCustomer.email.trim();

    if (!nameVal || !phoneVal) {
      showNotification("⚠️ Please fill in required fields (Name and Phone)", "error");
      return;
    }

    const payload = {
      name: nameVal,
      phone: phoneVal,
      customer_type: 'retail', // Default
    };

    // Add optional fields only if provided
    if (emailVal) {
      payload.email = emailVal;
    }

    try {
      const response = await customerAPI.createCustomer(payload);

      const data = response.data?.data || response.data;
      if (data) {
        // Parse joined_date properly
        let joinedDate = '';
        if (data.joined_date) {
          joinedDate = typeof data.joined_date === 'string' ? data.joined_date.split('T')[0] : new Date(data.joined_date).toISOString().split('T')[0];
        } else {
          joinedDate = new Date().toISOString().split('T')[0];
        }

        const mappedData = {
          ...data,
          joined: joinedDate,
          isActive: data.status === 'active'
        };
        const updatedCustomers = [mappedData, ...customers]; // Add to top
        setCustomers(updatedCustomers);
        showNotification(`✅ Customer "${data.name}" added successfully!`, "success");

        // Reset form
        setNewCustomer({
          name: "",
          email: "",
          phone: "",
          joined: new Date().toISOString().split("T")[0],
          isActive: true,
        });
        setShowAddModal(false);

        // Dispatch event for POS Billing page to update
        window.dispatchEvent(new CustomEvent("customerAdded", {
          detail: { customer: mappedData }
        }));
      }
    } catch (error) {
      let errorMessage = "Failed to create customer";

      // Helper function to extract error message from field
      const extractFieldError = (fieldErrors) => {
        if (!fieldErrors) return null;
        if (Array.isArray(fieldErrors)) {
          return fieldErrors.map(e => {
            // If it's an object, get the message property
            if (typeof e === 'object' && e.message) return e.message;
            if (typeof e === 'object' && e.detail) return e.detail;
            // Otherwise just convert to string
            return String(e);
          }).join(", ");
        }
        // If it's an object, try to get message
        if (typeof fieldErrors === 'object') {
          if (fieldErrors.message) return fieldErrors.message;
          if (fieldErrors.detail) return fieldErrors.detail;
          return JSON.stringify(fieldErrors);
        }
        return String(fieldErrors);
      };

      // Check for field-specific errors in response data
      const errorData = error.response?.data;

      if (errorData?.phone) {
        errorMessage = `Phone: ${extractFieldError(errorData.phone)}`;
      } else if (errorData?.gstin) {
        errorMessage = `GSTIN: ${extractFieldError(errorData.gstin)}`;
      } else if (errorData?.email) {
        errorMessage = `Email: ${extractFieldError(errorData.email)}`;
      } else if (errorData?.name) {
        errorMessage = `Name: ${extractFieldError(errorData.name)}`;
      } else if (errorData?.customer_type) {
        errorMessage = `Customer Type: ${extractFieldError(errorData.customer_type)}`;
      } else if (errorData?.detail) {
        // General detail error (could be string or object)
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(e => {
            return typeof e === 'object' ? JSON.stringify(e) : String(e);
          }).join("; ");
        } else if (typeof errorData.detail === 'object') {
          errorMessage = JSON.stringify(errorData.detail);
        } else {
          errorMessage = String(errorData.detail);
        }
      } else if (errorData?.non_field_errors) {
        errorMessage = extractFieldError(errorData.non_field_errors);
      } else if (error.message) {
        errorMessage = error.message;
      } else if (typeof errorData === 'object') {
        // Fallback: try to extract any error from the data object
        const firstErrorKey = Object.keys(errorData).find(key =>
          errorData[key] && (typeof errorData[key] === 'string' || Array.isArray(errorData[key]))
        );
        if (firstErrorKey) {
          errorMessage = `${firstErrorKey}: ${extractFieldError(errorData[firstErrorKey])}`;
        }
      }

      showNotification(`Failed to add customer: ${errorMessage}`, "error");
    }
  }

  const exportCustomersToCSV = () => {
    try {
      const data = customers.map(c => ({
        ID: c.id,
        Name: c.name,
        Email: c.email,
        Phone: c.phone,
        "Joined Date": c.joined,
      }));

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Customers");

      // Set column widths
      worksheet["!cols"] = [
        { wch: 12 },
        { wch: 20 },
        { wch: 25 },
        { wch: 18 },
        { wch: 12 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
      ];

      const timestamp = new Date().toISOString().split("T")[0];
      XLSX.writeFile(workbook, `customers_${timestamp}.xlsx`);
      try { exportSuccess.showExportSuccess(`${customers.length} customers exported`); } catch (e) { }
    } catch (error) {
      showNotification("❌ Error exporting customers to CSV", "error");
    }
  };

  const importCustomersFromCSV = (event) => {
    try {
      const file = event.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          showNotification("⚠️ CSV file is empty", "error");
          return;
        }

        const importedCustomers = jsonData.map(row => ({
          id: row.ID || `c${Date.now()}_${Math.random()}`,
          name: row.Name || "",
          email: row.Email || "",
          phone: row.Phone || "",
          joined: row["Joined Date"] || new Date().toISOString().split("T")[0],
        }));

        // Filter out invalid entries
        const validCustomers = importedCustomers.filter(c => c.name && c.email && c.phone);

        if (validCustomers.length === 0) {
          showNotification("⚠️ No valid customers found in CSV file", "error");
          return;
        }

        // Merge with existing (avoid duplicates by ID)
        const existingIds = new Set(customers.map(c => c.id));
        const newCustomers = validCustomers.filter(c => !existingIds.has(c.id));

        setCustomers([...customers, ...newCustomers]);
        try { exportSuccess.showExportSuccess(`${newCustomers.length} customers imported`); } catch (e) { }
      };

      reader.readAsArrayBuffer(file);
      event.target.value = "";
    } catch (error) {
      showNotification("❌ Error importing customers from CSV", "error");
    }
  };

  // Check if user has permission to view customers
  if (!hasPermission(userRole, 'view_customers')) {
    return (
      <div className="min-h-screen bg-light dark:bg-dark-bg p-4 md:p-8 flex items-center justify-center">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-8 text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-600 dark:text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-red-900 dark:text-red-200 mb-2">Access Denied</h2>
          <p className="text-red-700 dark:text-red-300">You don't have permission to view customers. Please contact your administrator.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-light dark:bg-dark-bg p-4 md:p-8 transition-colors">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white">Customers</h1>
            <p className="text-gray-600 dark:text-gray-300 mt-2">Manage your customer relationships</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {hasPermission(userRole, 'manage_customers') && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowAddModal(true)}
                className="hidden md:flex items-center gap-2 px-6 py-3 bg-gradient-primary text-white rounded-xl font-bold hover:shadow-lg transition-shadow"
              >
                <Plus className="w-5 h-5" />
                Add Customer
              </motion.button>
            )}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={exportCustomersToCSV}
              className="hidden md:flex items-center gap-2 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-bold transition-shadow hover:shadow-lg"
            >
              <Download className="w-5 h-5" />
              Export CSV
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="hidden md:flex items-center gap-2 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-bold transition-shadow hover:shadow-lg cursor-pointer"
              onClick={() => document.getElementById("csv-import-input").click()}
            >
              <Upload className="w-5 h-5" />
              Import CSV
            </motion.button>
            <input
              id="csv-import-input"
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={importCustomersFromCSV}
              className="hidden"
            />
          </div>
        </div>
      </motion.div>

      {/* Search Bar with Calendar */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6 flex flex-col md:flex-row gap-3 items-stretch md:items-center"
      >
        <div className="relative flex-1">
          <Search className="absolute left-4 top-3.5 w-5 h-5 text-gray-400" />
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search customers..."
            className="w-full pl-12 pr-4 py-3 rounded-xl border border-blue-200 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          />
        </div>

        {/* Date Selector */}
        <div className="flex gap-2 items-center flex-shrink-0">
          <Calendar className="w-5 h-5 text-gray-400" />
          <select
            value={String(selectedDate.getDate())}
            onChange={(e) => {
              const newDate = new Date(selectedDate);
              newDate.setDate(parseInt(e.target.value));
              setSelectedDate(newDate);
              setDateFilterActive(true);
            }}
            className="px-2 py-3 rounded-lg border border-blue-200 dark:border-dark-border bg-white dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm font-medium w-16"
          >
            {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
              <option key={day} value={String(day)}>{day}</option>
            ))}
          </select>
          <select
            value={String(selectedDate.getMonth())}
            onChange={(e) => {
              const newDate = new Date(selectedDate);
              newDate.setMonth(parseInt(e.target.value));
              setSelectedDate(newDate);
              setDateFilterActive(true);
            }}
            className="px-2 py-3 rounded-lg border border-blue-200 dark:border-dark-border bg-white dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm font-medium w-20"
          >
            {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((month, idx) => (
              <option key={idx} value={String(idx)}>{month}</option>
            ))}
          </select>
          <select
            value={String(selectedDate.getFullYear())}
            onChange={(e) => {
              const newDate = new Date(selectedDate);
              newDate.setFullYear(parseInt(e.target.value));
              setSelectedDate(newDate);
              setDateFilterActive(true);
            }}
            className="px-2 py-3 rounded-lg border border-blue-200 dark:border-dark-border bg-white dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm font-medium w-20"
          >
            {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i).map(year => (
              <option key={year} value={String(year)}>{year}</option>
            ))}
          </select>
          <button
            onClick={() => setDateFilterActive(false)}
            className="px-3 py-3 rounded-lg bg-blue-200 hover:bg-blue-300 text-blue-900 dark:bg-blue-900/40 dark:text-blue-200 text-sm font-medium transition-colors"
          >
            Clear
          </button>
        </div>
      </motion.div>

      {loading ? (
        <EmptyState
          preset="people"
          title="Loading customers..."
          message="Please wait a moment while we fetch your customer directory."
          className="py-12"
        />
      ) : !Array.isArray(filtered) || filtered.length === 0 ? (
        <EmptyState
          preset="people"
          title="No customers found"
          message={searchTerm ? `No customers matching "${searchTerm}"` : "Click 'Add Customer' to add your first customer to the directory."}
          className="py-12"
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((customer, idx) => {
          // Skip if customer is not a valid object with required properties
          if (!customer || typeof customer !== 'object' || !customer.id) {
            return null;
          }
          // Enrich customer with real invoice data
          const enrichedCustomer = enrichCustomerWithInvoiceData(customer);
          return (
            <motion.div
              key={customer.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              whileHover={{ y: -8 }}
              className="bg-white dark:bg-dark-card rounded-2xl border border-blue-200 dark:border-dark-border shadow-md hover:shadow-xl p-6 transition-all group"
            >
              <div 
                className="cursor-pointer" 
                onClick={() => setViewingCustomer(enrichedCustomer)}
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-800 shadow-sm">
                      <User className="w-6 h-6" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white line-clamp-1">{enrichedCustomer.name}</h3>
                      </div>
                      <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">
                        {enrichedCustomer.customer_id || "ID-PENDING"}
                      </p>
                    </div>
                  </div>
                  
                  {hasPermission(userRole, 'manage_customers') && (
                    <div className="flex gap-2">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={(e) => { e.stopPropagation(); setEditing(customer); }}
                        className="p-2 rounded-xl bg-gray-50 dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-blue-900/30 text-gray-400 hover:text-blue-600 transition-all border border-gray-100 dark:border-gray-700"
                      >
                        <Edit2 className="w-4 h-4" />
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={(e) => { e.stopPropagation(); deleteCustomer(customer.id); }}
                        className="p-2 rounded-xl bg-gray-50 dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-600 transition-all border border-gray-100 dark:border-gray-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </motion.button>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-gray-50/50 dark:bg-gray-800/30 p-4 rounded-2xl border border-gray-100 dark:border-gray-800">
                    <div className="flex items-center gap-2 text-gray-400 mb-1">
                      <ShoppingBag className="w-3.5 h-3.5" />
                      <span className="text-[10px] font-black uppercase tracking-widest">Total Orders</span>
                    </div>
                    <p className="text-xl font-black text-gray-900 dark:text-white">{enrichedCustomer.total_orders || 0}</p>
                  </div>
                  <div className="bg-gray-50/50 dark:bg-gray-800/30 p-4 rounded-2xl border border-gray-100 dark:border-gray-800">
                    <div className="flex items-center gap-2 text-gray-400 mb-1">
                      <IndianRupee className="w-3.5 h-3.5" />
                      <span className="text-[10px] font-black uppercase tracking-widest">Total Spent</span>
                    </div>
                    <p className="text-xl font-black text-green-600">₹{parseFloat(enrichedCustomer.total_spent || 0).toLocaleString()}</p>
                  </div>
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-3 text-gray-500">
                      <Phone className="w-4 h-4" />
                      <span className="font-medium">{enrichedCustomer.phone}</span>
                    </div>
                    <div className="flex items-center gap-2 px-2 py-1 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-lg text-[10px] font-bold uppercase tracking-wider">
                      <CheckCircle className="w-3 h-3" />
                      Active
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-gray-500 text-sm">
                    <Clock className="w-4 h-4" />
                    <span className="font-medium">
                      Last Visit: {enrichedCustomer.last_visit_date ? new Date(enrichedCustomer.last_visit_date).toLocaleDateString() : 'No visits yet'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-100 dark:border-gray-800">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setViewingCustomer(enrichedCustomer)}
                  className="w-full py-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-sm font-bold flex items-center justify-center gap-2 transition-all hover:bg-blue-100 dark:hover:bg-blue-900/40"
                >
                  View Details
                  <ArrowRight className="w-4 h-4" />
                </motion.button>
              </div>
            </motion.div>
          );
        })}
      </div>
      )}

      {/* Viewing / Detail Modal */}
      {viewingCustomer && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setViewingCustomer(null)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white dark:bg-dark-card rounded-[2.5rem] max-w-4xl w-full shadow-2xl overflow-hidden flex flex-col md:flex-row max-h-[90vh]"
          >
            {/* Left Sidebar - Profile Summary */}
            <div className="w-full md:w-80 bg-gray-50 dark:bg-gray-900/50 p-8 border-r border-gray-100 dark:border-gray-800">
              <div className="flex flex-col items-center text-center">
                <div className="w-24 h-24 rounded-3xl bg-blue-600 flex items-center justify-center text-white text-3xl font-black mb-4 shadow-lg shadow-blue-200 dark:shadow-none">
                  {viewingCustomer.name[0].toUpperCase()}
                </div>
                <h2 className="text-2xl font-black text-gray-900 dark:text-white leading-tight mb-1">{viewingCustomer.name}</h2>
                <div className="bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest mb-6">
                  {viewingCustomer.customer_id || "Customer"}
                </div>

                <div className="w-full space-y-4 text-left">
                  <div className="p-4 bg-white dark:bg-dark-bg rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Phone Number</p>
                    <div className="flex items-center gap-3 text-gray-900 dark:text-white font-bold">
                      <Phone className="w-4 h-4 text-blue-500" />
                      {viewingCustomer.phone}
                    </div>
                  </div>
                  {viewingCustomer.email && (
                    <div className="p-4 bg-white dark:bg-dark-bg rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm">
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Email Address</p>
                      <div className="flex items-center gap-3 text-gray-900 dark:text-white font-bold truncate">
                        <Mail className="w-4 h-4 text-blue-500" />
                        <span className="truncate">{viewingCustomer.email}</span>
                      </div>
                    </div>
                  )}
                  <div className="p-4 bg-white dark:bg-dark-bg rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Joined Date</p>
                    <div className="flex items-center gap-3 text-gray-900 dark:text-white font-bold">
                      <Calendar className="w-4 h-4 text-blue-500" />
                      {new Date(viewingCustomer.joined_date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Insights & History */}
            <div className="flex-1 p-8 overflow-y-auto">
              <div className="flex justify-between items-center mb-8">
                <h3 className="text-xl font-black text-gray-900 dark:text-white flex items-center gap-2">
                  <LayoutGrid className="w-5 h-5 text-blue-600" />
                  Purchase Dashboard
                </h3>
                <button 
                  onClick={() => setViewingCustomer(null)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
                >
                  <X className="w-6 h-6 text-gray-400" />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10">
                <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-6 rounded-3xl text-white shadow-xl shadow-blue-100 dark:shadow-none">
                  <div className="flex items-center gap-2 opacity-80 mb-2">
                    <ShoppingBag className="w-4 h-4" />
                    <span className="text-xs font-bold uppercase tracking-widest">Total Orders</span>
                  </div>
                  <p className="text-4xl font-black">{viewingCustomer.total_orders || 0}</p>
                </div>
                <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-3xl text-white shadow-xl shadow-green-100 dark:shadow-none">
                  <div className="flex items-center gap-2 opacity-80 mb-2">
                    <IndianRupee className="w-4 h-4" />
                    <span className="text-xs font-bold uppercase tracking-widest">Total Spend</span>
                  </div>
                  <p className="text-4xl font-black">₹{parseFloat(viewingCustomer.total_spent || 0).toLocaleString()}</p>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-black text-gray-400 uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Recent Invoice History
                </h4>
                
                <div className="space-y-3">
                  {!viewingCustomer.recent_invoices || viewingCustomer.recent_invoices.length === 0 ? (
                    <div className="py-12 flex flex-col items-center justify-center text-gray-400 grayscale">
                      <ShoppingBag className="w-12 h-12 opacity-20 mb-3" />
                      <p className="font-bold">No purchase history found</p>
                    </div>
                  ) : (
                    viewingCustomer.recent_invoices.map((inv) => (
                      <div key={inv.id} className="flex items-center justify-between p-5 bg-gray-50 dark:bg-gray-800/40 rounded-2xl border border-gray-100 dark:border-gray-800 hover:border-blue-200 transition-all group">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-xl bg-white dark:bg-dark-bg flex items-center justify-center text-blue-600 font-bold border border-gray-100 dark:border-gray-700">
                            #
                          </div>
                          <div>
                            <p className="font-black text-gray-900 dark:text-white uppercase tracking-wider">{inv.invoice_number}</p>
                            <p className="text-xs text-gray-500 font-medium">
                              {new Date(inv.invoice_date).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-black text-gray-900 dark:text-white">₹{parseFloat(inv.total_amount).toLocaleString()}</p>
                          <span className={`text-[10px] font-black uppercase tracking-widest ${inv.status === 'completed' ? 'text-green-500' : 'text-orange-500'}`}>
                            {inv.status}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                
                <motion.button
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  className="w-full mt-6 py-4 rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-800 text-gray-400 dark:text-gray-500 text-xs font-bold hover:text-blue-500 hover:border-blue-200 transition-all uppercase tracking-widest"
                >
                  View Full History in Invoices
                </motion.button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Edit Modal */}
      {
        editing && hasPermission(userRole, 'manage_customers') && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setEditing(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-dark-card rounded-2xl p-8 max-w-md w-full shadow-xl"
            >
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Edit Customer</h2>
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Customer Name</label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Email</label>
                  <input
                    type="email"
                    className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                    value={editing.email}
                    onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Phone</label>
                  <input
                    type="tel"
                    className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                    value={editing.phone}
                    onChange={(e) => setEditing({ ...editing, phone: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => saveCustomer(editing)}
                  className="flex-1 py-3 rounded-xl bg-gradient-primary text-white font-bold hover:shadow-lg transition-shadow"
                >
                  Save Changes
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setEditing(null)}
                  className="flex-1 py-3 rounded-xl border-2 border-gray-200 dark:border-dark-border text-gray-900 dark:text-white font-bold hover:bg-gray-50 dark:hover:bg-dark-bg transition-colors"
                >
                  Cancel
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )
      }

      {/* Add Customer Modal */}
      {
        showAddModal && hasPermission(userRole, 'manage_customers') && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-dark-card rounded-2xl p-8 max-w-md w-full shadow-xl max-h-96 overflow-y-auto"
            >
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Add New Customer</h2>
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Customer Name *</label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                    value={newCustomer.name}
                    onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                    placeholder="e.g., ABC Restaurant"
                  />
                </div>


                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Email Address (Optional)</label>
                  <input
                    type="email"
                    className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                    value={newCustomer.email}
                    onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                    placeholder="e.g., customer@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Phone Number *</label>
                  <input
                    type="tel"
                    className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                    value={newCustomer.phone}
                    onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                    placeholder="e.g., +91 9876543210"
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={addNewCustomer}
                  className="flex-1 py-3 rounded-xl bg-gradient-primary text-white font-bold hover:shadow-lg transition-shadow"
                >
                  Add Customer
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 py-3 rounded-xl border-2 border-gray-200 dark:border-dark-border text-gray-900 dark:text-white font-bold hover:bg-gray-50 dark:hover:bg-dark-bg transition-colors"
                >
                  Cancel
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )
      }

      {/* Delete Confirmation Modal */}
      {
        deleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setDeleteConfirm(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-dark-card rounded-2xl p-8 max-w-md w-full shadow-xl"
            >
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 mx-auto mb-4">
                <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white text-center mb-2">Delete Customer?</h2>
              <p className="text-gray-600 dark:text-gray-300 text-center mb-6">
                Are you sure you want to delete <span className="font-bold">{deleteConfirm.name}</span>? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => confirmDelete(deleteConfirm.id)}
                  className="flex-1 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold transition-colors"
                >
                  Yes, Delete
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 py-3 rounded-xl border-2 border-gray-200 dark:border-dark-border text-gray-900 dark:text-white font-bold hover:bg-gray-50 dark:hover:bg-dark-bg transition-colors"
                >
                  Cancel
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )
      }

      {/* Notification Toast */}
      {
        notification && notification.type === "success" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 100, damping: 15 }}
              className="bg-white dark:bg-dark-card rounded-3xl p-12 shadow-2xl text-center max-w-sm w-full mx-4"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 15 }}
                className="w-24 h-24 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-6"
              >
                <CheckCircle className="w-16 h-16 text-blue-600" strokeWidth={1.5} />
              </motion.div>
              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-3xl font-bold text-gray-900 dark:text-white mb-2"
              >
                Success
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="text-gray-600 dark:text-gray-300 text-lg"
              >
                {notification.message}
              </motion.p>
            </motion.div>
          </motion.div>
        )
      }

      {/* Delete Success Notification */}
      {
        notification && notification.type === "delete" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 100, damping: 15 }}
              className="bg-white dark:bg-dark-card rounded-3xl p-12 shadow-2xl text-center max-w-sm w-full mx-4"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 15 }}
                className="w-24 h-24 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6"
              >
                <Trash2 className="w-16 h-16 text-red-600" strokeWidth={1.5} />
              </motion.div>
              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-3xl font-bold text-gray-900 dark:text-white mb-2"
              >
                Customer Deleted
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="text-gray-600 dark:text-gray-300 text-lg"
              >
                The customer has been permanently removed
              </motion.p>
            </motion.div>
          </motion.div>
        )
      }

      {
        notification && (notification.type === "error" || (notification.type !== "success" && notification.type !== "delete")) && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-4 right-4 z-50 bg-white dark:bg-dark-card border border-red-200 dark:border-red-800 rounded-xl shadow-2xl p-4 flex items-center gap-3 max-w-md"
          >
            <div className="bg-red-100 dark:bg-red-900/30 p-2 rounded-full flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h4 className="font-bold text-gray-900 dark:text-white text-sm">Action Failed</h4>
              <p className="text-gray-600 dark:text-gray-300 text-sm">{notification.message}</p>
            </div>
          </motion.div>
        )
      }
    </div >
  );
}
